import { FlexBox, IconButton, Text, Tooltip } from '@dixa/lib-shared-habla';
import { MouseEventHandler, useMemo } from 'react';

import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationIdActionConfiguration } from './conversationId.types';

type ConversationIdProps = {
  /** The conversation id. */
  csid: number;
  /** The associated actions with the conversation. */
  actions?: ConversationIdActionConfiguration;
};

const defaultActions: ConversationIdActionConfiguration = {
  link: true,
};

/**
 * Represents the component for displaying the conversation id and various quick
 * actions associated with that conversation.
 */
export const ConversationId = (props: ConversationIdProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { csid } = props;
  const actions = Object.assign({}, defaultActions, props.actions);

  /**
   * Represents the csid element including whether or the element should be a
   * link or not based on the actions configuration.
   */
  const csidElement = useMemo(() => {
    const text = `#${csid}`;

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
      e.stopPropagation();

      if (actions.link && typeof actions.link !== 'boolean') {
        actions.link.onClick?.();
      }
    };

    return typeof actions.link === 'undefined' ? (
      <Text.Paragraph>{text}</Text.Paragraph>
    ) : (
      <AppLink
        path="/conversation/:csid"
        params={{ path: { csid: csid.toString() } }}
        onClick={handleClick}
        target={
          typeof actions.link !== 'boolean' && actions.link?.openInNewTab
            ? '_blank'
            : '_self'
        }
        data-testid="conversation-id-link"
      >
        <Text.ExternalLink as="span">{text}</Text.ExternalLink>
      </AppLink>
    );
  }, [csid, actions.link]);

  /** Represents the preview element. */
  const previewElement = useMemo(() => {
    if (!actions.preview) {
      return null;
    }

    const handlePreview = () => {
      dispatch(
        appActions.showModal({
          modal: { type: 'app:conversationPreview', arg: { csid } },
        }),
      );

      if (actions.preview && typeof actions.preview !== 'boolean') {
        actions.preview.onClick?.();
      }
    };

    return (
      <Tooltip content="Preview conversation">
        <IconButton icon="eye" variant="tertiary" onClick={handlePreview} />
      </Tooltip>
    );
  }, [actions.preview, csid, dispatch]);

  return (
    <FlexBox gap="200" alignItems="center" style={{ display: 'inline-flex' }}>
      {csidElement}
      {previewElement}
    </FlexBox>
  );
};
